import React from 'react';
import styled, { css } from 'styled-components';

import colors from '@front/common/src/utils/colors';
import { useModalObject } from '@front/common/src/hooks/use-modal';
import { getCurrentCountry } from '@front/common/src/utils/country';

import {
  getFormattedPrice,
  getFormattedPriceForMultiple,
  getFormattedPromotion,
} from '@front/customer/src/utils/cleaning';
import { StandardPrices } from '@front/customer/src/types/prices';
import { CONFIG_BY_COUNTRY } from '@front/customer/src/constants/country-config';

import { translate } from '@front/i18n';

import Text from '@front/web-ui/src/components/base-text/variants/text';
import Separator from '@front/web-ui/src/components/separator/separator';
import TextButton from '@front/web-ui/src/components/button/text-button';
import Skeleton from '@front/web-ui/src/components/skeleton/skeleton';

import ServiceFeeIncludedText from '../../service-fee-included-text';
import TaxModal from '../common/tax-modal';
import Reduction from '../common/reduction';

const t = translate('basket');

const calculationLineStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  padding: 10px 0;
  white-space: nowrap;
`;

const PromotionLine = styled(Reduction)`
  ${calculationLineStyle}
`;

const CalculationLine = styled.div`
  ${calculationLineStyle}
`;

type MultiplePricesWithTaxProps = {
  hasDuration: boolean;
  hasPrestations: boolean;
  isLoading: boolean;
  prices?: StandardPrices;
};

const MultiplePricesWithTax = ({ hasDuration, hasPrestations, isLoading, prices }: MultiplePricesWithTaxProps) => {
  const { isOpen, open, close } = useModalObject();
  const { hasTaxCreditInBasket } = CONFIG_BY_COUNTRY[getCurrentCountry()];

  const hasPromo = !!prices?.promotion;

  return (
    <>
      <TaxModal onClose={close} isOpen={isOpen} />
      {hasPromo && (
        <>
          <Separator />
          <PromotionLine title={t('.promo_code')} reduction={getFormattedPromotion(hasDuration, prices?.promotion)} />
        </>
      )}
      {hasPrestations && (
        <>
          <Separator />
          <CalculationLine>
            <Text color={colors.ink100}>{t('.total')}</Text>
            {isLoading ? (
              <Skeleton linesWidth={['50px']} />
            ) : (
              <Text color={colors.ink100}>{getFormattedPriceForMultiple(hasDuration, prices?.total)}</Text>
            )}
          </CalculationLine>
          {hasTaxCreditInBasket && (
            <>
              <Separator />
              <CalculationLine>
                <TextButton onClick={open} label={t('.cleaning.services.tax.label')}>
                  {t('.cleaning.services.tax.label')}
                </TextButton>
                {isLoading ? (
                  <Skeleton linesWidth={['50px']} />
                ) : (
                  <Text>{`- ${getFormattedPrice(hasDuration, prices?.tax_credit)}`}</Text>
                )}
              </CalculationLine>
              <Separator />
              <CalculationLine>
                <Text.MDBold>{t('.after_tax_credit')}</Text.MDBold>
                {isLoading ? (
                  <Skeleton linesWidth={['50px']} />
                ) : (
                  <Text.MDBold>{getFormattedPriceForMultiple(hasDuration, prices?.after_tax)}</Text.MDBold>
                )}
              </CalculationLine>
            </>
          )}
        </>
      )}
      <ServiceFeeIncludedText />
    </>
  );
};

export default MultiplePricesWithTax;
